import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { deletePatient } from '../../api/services/patient';
import AVATARS from '../../assets/avatars';
import { hasInternetAsync } from './helpers';
import { SmallButton } from '@/components/Button';
import { logError } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 24,
    gap: 32,
  },
  rowWithCenterItems: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  squareImage: {
    width: 56,
    height: 56,
  },
  largeText: {
    fontSize: 24,
  },
  gapStyle: {
    gap: 16,
  },
  reducedWidth: {
    padding: 16,
    width: '66%',
  },
  rowStyle: {
    flexDirection: 'row',
  },
  semiboldText: {
    fontSize: 14,
    fontWeight: '600',
    width: 112,
    flexShrink: 0,
  },
  rowText: {
    flexGrow: 0,
  },
  horizontalLine: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 8,
  },
  withBiography: { paddingBottom: 32 },
});

export function DashboardHeader({
  _id,
  avatar,
  fullName,
  diagnosis,
  biography,
  fetchPatients,
  navigation,
}) {
  const avatarArray = avatar?.[0].split('/');
  const image =
    avatarArray?.length && avatarArray[avatarArray?.length - 1].split('.')[0];
  const source = AVATARS[image];
  const { isTherapistDemo } = useStoreState((store) => store);
  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = useCallback(async () => {
    try {
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        setShowDeleteModal(true);
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    } catch (error) {
      logError('handleDeleteClick in DashboardHeader', error);
    }
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await deletePatient(_id);
      setShowDeleteModal(false);
      await fetchPatients(null, null);
      navigation.navigate('Home');
    } catch (error) {
      logError('Could not delete patient', error);
    }
  }, [_id]);

  const handleEditClick = useCallback(async () => {
    try {
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        navigation.push('AddPatientScreen', { _id });
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    } catch (error) {
      logError('Could not edit patient without internet connection', error);
    }
  }, [_id]);

  return (
    <>
      <Modal
        isVisible={showDeleteModal}
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="secondary"
            onPress={handleDeleteCancel}
          />
        }
        confirmActionButton={
          <Button
            label="Löschen"
            look="danger"
            onPress={handleDeleteConfirm}
            redBackground={true}
          />
        }
        onClose={() => setShowDeleteModal(false)}
        title="Patienten-Profil löschen"
      >
        <Text style={{ fontSize: 20 }}>
          Möchten Sie das Patienten-Profil {fullName} wirklich löschen? Das
          Löschen ist unwiderruflich.
        </Text>
      </Modal>

      <View style={styles.container}>
        <View style={styles.rowWithCenterItems}>
          <Image style={styles.squareImage} source={source} />
          <View style={{ flex: 1 }}>
            <Text
              style={styles.largeText}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {fullName}
            </Text>
          </View>
        </View>
        <View style={styles.gapStyle}>
          {!isTherapistDemo && (
            <>
              <SmallButton
                label="bearbeiten"
                icon="edit"
                onPress={handleEditClick}
              />
              <SmallButton
                label="löschen"
                icon="trash"
                onPress={handleDeleteClick}
              />
            </>
          )}
        </View>
      </View>
      <View style={[styles.reducedWidth, !biography && styles.withBiography]}>
        {!!diagnosis && (
          <View style={styles.rowStyle}>
            <Text style={styles.semiboldText}>Diagnose:</Text>
            <Text style={styles.rowText} numberOfLines={1} ellipsizeMode="tail">
              {diagnosis.replace(/(\r\n|\n|\r)/gm, ' ')}
            </Text>
          </View>
        )}
        {!!biography && (
          <>
            <View style={styles.horizontalLine} />
            <View style={styles.rowStyle}>
              <Text style={styles.semiboldText}>Kommentar:</Text>
              <Text
                style={styles.rowText}
                numberOfLines={5}
                ellipsizeMode="tail"
              >
                {biography.replace(/(\r\n|\n|\r)/gm, ' ')}
              </Text>
            </View>
          </>
        )}
      </View>
    </>
  );
}
